import React, { useState, useEffect } from 'react';
import { X, Bell, Loader2 } from 'lucide-react';
import { useGetBaseUnitAlarmConfigQuery, useUpsertBaseUnitAlarmConfigMutation } from '../../services/baseUnitAlarmApi';
import { useGetUsersQuery } from '../../services/userApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  baseUnit: { serialNo: number; base_unit_name: string; type: number } | null;
}

interface RecipientState {
  user_id: number;
  notify_email: boolean;
  notify_sms: boolean;
}

export default function BaseUnitAlarmDialog({ isOpen, onClose, baseUnit }: Props) {
  const serial = baseUnit ? String(baseUnit.serialNo) : '';
  const isTS1 = baseUnit?.type === 1;

  const { data, isLoading } = useGetBaseUnitAlarmConfigQuery(serial, { skip: !isOpen || !serial });
  const { data: users, isLoading: usersLoading } = useGetUsersQuery(undefined, { skip: !isOpen });
  const [upsert, { isLoading: isSaving }] = useUpsertBaseUnitAlarmConfigMutation();

  const [disconnectedEnabled, setDisconnectedEnabled] = useState(false);
  const [disconnectedDelay, setDisconnectedDelay] = useState(30);
  const [powerEnabled, setPowerEnabled] = useState(false);
  const [repeatEnabled, setRepeatEnabled] = useState(true);
  const [repeatInterval, setRepeatInterval] = useState(60);
  const [recipients, setRecipients] = useState<RecipientState[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    const cfg = data.config;
    setDisconnectedEnabled(cfg?.disconnected_enabled ?? false);
    setDisconnectedDelay(cfg?.disconnected_delay_mins ?? 30);
    setPowerEnabled(cfg?.power_alarm_enabled ?? false);
    setRepeatEnabled(cfg?.repeat_enabled ?? true);
    setRepeatInterval(cfg?.repeat_interval_mins ?? 60);
    setRecipients(
      (data.recipients ?? []).map(r => ({
        user_id: r.user_id,
        notify_email: r.notify_email,
        notify_sms: r.notify_sms,
      }))
    );
  }, [data]);

  if (!isOpen || !baseUnit) return null;

  const toggleRecipient = (userId: number) => {
    setRecipients(prev => {
      const exists = prev.find(r => r.user_id === userId);
      if (exists) return prev.filter(r => r.user_id !== userId);
      return [...prev, { user_id: userId, notify_email: false, notify_sms: false }];
    });
  };

  const updateRecipient = (userId: number, field: 'notify_email' | 'notify_sms', value: boolean) => {
    setRecipients(prev => prev.map(r => r.user_id === userId ? { ...r, [field]: value } : r));
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaved(false);
    try {
      await upsert({
        serial,
        disconnected_enabled: disconnectedEnabled,
        disconnected_delay_mins: disconnectedDelay,
        power_alarm_enabled: powerEnabled,
        repeat_enabled: repeatEnabled,
        repeat_interval_mins: repeatInterval,
        recipients,
      }).unwrap();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaveError('Failed to save alarm settings. Please try again.');
    }
  };

  const inputClass = 'w-20 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const toggleClass = (on: boolean) =>
    `relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${on ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg min-w-0 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Bell className="h-5 w-5 text-indigo-500" />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Alarm Settings</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{baseUnit.base_unit_name} · {serial}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto overflow-x-hidden flex-1 px-5 py-4 space-y-5 min-w-0">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
          ) : (
            <>
              {/* Disconnected alarm */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Disconnected Alarm</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Trigger when base unit hasn't connected for the set time</p>
                  </div>
                  <button type="button" role="switch" aria-checked={disconnectedEnabled} onClick={() => setDisconnectedEnabled(v => !v)} className={toggleClass(disconnectedEnabled)}>
                    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${disconnectedEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
                {disconnectedEnabled && (
                  <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span>Trigger after</span>
                    <input type="number" min={1} max={1440} value={disconnectedDelay} onChange={e => setDisconnectedDelay(Number(e.target.value))} className={inputClass} />
                    <span>minutes offline</span>
                  </div>
                )}
              </div>

              {/* Power alarm — TS1 only */}
              {isTS1 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Power Alarm</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Trigger when not running on mains/USB power</p>
                    </div>
                    <button type="button" role="switch" aria-checked={powerEnabled} onClick={() => setPowerEnabled(v => !v)} className={toggleClass(powerEnabled)}>
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${powerEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Repeat notifications */}
              {(disconnectedEnabled || powerEnabled) && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Repeat Notifications</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Re-notify while alarm condition persists</p>
                    </div>
                    <button type="button" role="switch" aria-checked={repeatEnabled} onClick={() => setRepeatEnabled(v => !v)} className={toggleClass(repeatEnabled)}>
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${repeatEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                  {repeatEnabled && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span>Repeat every</span>
                      <input type="number" min={1} max={1440} value={repeatInterval} onChange={e => setRepeatInterval(Number(e.target.value))} className={inputClass} />
                      <span>minutes</span>
                    </div>
                  )}
                </div>
              )}

              {/* Recipients */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Notification Recipients</p>
                {usersLoading ? (
                  <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>
                ) : !users?.length ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No users found.</p>
                ) : (
                  <div className="space-y-2 min-w-0">
                    <div className="grid grid-cols-[minmax(0,1fr)_2.25rem_2.25rem] gap-x-2 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:gap-x-3 items-center text-xs text-gray-500 dark:text-gray-400 pb-1 border-b border-gray-100 dark:border-gray-700 min-w-0">
                      <span className="min-w-0">User</span>
                      <span className="text-center justify-self-center">Email</span>
                      <span className="text-center justify-self-center">SMS</span>
                    </div>
                    {users.map(user => {
                      const recipient = recipients.find(r => r.user_id === user.id);
                      const selected = !!recipient;
                      return (
                        <div
                          key={user.id}
                          className="grid grid-cols-[minmax(0,1fr)_2.25rem_2.25rem] gap-x-2 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:gap-x-3 items-center min-w-0"
                        >
                          <label className="flex items-start gap-2 cursor-pointer min-w-0">
                            <input type="checkbox" checked={selected} onChange={() => toggleRecipient(user.id)} className="mt-1 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm text-gray-800 dark:text-gray-200 break-words">
                                {user.firstName} {user.lastName}
                              </span>
                              {user.email && (
                                <span className="block text-xs text-gray-400 truncate" title={user.email}>
                                  {user.email}
                                </span>
                              )}
                            </span>
                          </label>
                          <input
                            type="checkbox"
                            checked={recipient?.notify_email ?? false}
                            disabled={!selected}
                            onChange={e => updateRecipient(user.id, 'notify_email', e.target.checked)}
                            className="shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-30 justify-self-center"
                          />
                          <input
                            type="checkbox"
                            checked={recipient?.notify_sms ?? false}
                            disabled={!selected || !user.phoneNumber}
                            onChange={e => updateRecipient(user.id, 'notify_sms', e.target.checked)}
                            className="shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-30 justify-self-center"
                            title={!user.phoneNumber ? 'No mobile number on file' : undefined}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between min-w-0">
          <div className="min-w-0 sm:flex-1">
            {saveError && <p className="text-sm text-red-600 dark:text-red-400 break-words">{saveError}</p>}
            {saved && <p className="text-sm text-green-600 dark:text-green-400">Saved.</p>}
          </div>
          <div className="flex gap-2 justify-end shrink-0">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving || isLoading} className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5">
              {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
